import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { messages } from './locales/index.js';
import { DEFAULT_UI_LOCALE, isUiLocale } from './uiLanguages.js';

const STORAGE_KEY = 'pulinaLocale';

const I18nContext = createContext(null);

function readStoredLocale() {
  if (typeof window === 'undefined') return DEFAULT_UI_LOCALE;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isUiLocale(stored) ? stored : DEFAULT_UI_LOCALE;
  } catch {
    return DEFAULT_UI_LOCALE;
  }
}

function persistLocale(locale) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // ignore
  }
  try {
    document.documentElement.lang = locale;
  } catch {
    // ignore
  }
}

export function I18nProvider({ children, initialLocale }) {
  const [locale, setLocaleState] = useState(() => {
    if (initialLocale && isUiLocale(initialLocale)) return initialLocale;
    return readStoredLocale();
  });

  useEffect(() => {
    persistLocale(locale);
  }, [locale]);

  useEffect(() => {
    if (initialLocale && isUiLocale(initialLocale) && initialLocale !== locale) {
      setLocaleState(initialLocale);
    }
  }, [initialLocale, locale]);

  const setLocale = useCallback((next) => {
    if (!isUiLocale(next)) return;
    setLocaleState(next);
    persistLocale(next);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('pulina-locale-changed', { detail: { locale: next } }));
    }
  }, []);

  const t = useCallback(
    (key, fallback = key) => {
      const pack = messages[locale] || messages[DEFAULT_UI_LOCALE] || messages.fi;
      if (pack && pack[key] != null) return pack[key];
      const fiPack = messages.fi;
      if (fiPack && fiPack[key] != null) return fiPack[key];
      return fallback;
    },
    [locale],
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}

export function useT() {
  return useI18n().t;
}

export { readStoredLocale, persistLocale, STORAGE_KEY as LOCALE_STORAGE_KEY };
